import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import Auth from '@/amplify/auth';

export const AUTHENTICATOR_AUTHSTATE = 'amplify-authenticator-authState';

/**
 * Replaces <Authenticator hideDefault> from aws-amplify-react@3 (EOL, no version available for Amplify v6).
 *
 * Three contracts that must be preserved, verified against Authenticator's source code:
 *  1. The `authState` / `onStateChange` props passed down to App (which App forwards to ~25 routes).
 *  2. The localStorage key `amplify-authenticator-authState` — directly read by HeaderTop.js:845, 
 *     ConfirmAnonymousQA.js:172, and LogKibanaHandler.js:35 instead of passing via props.
 *  3. The root wrapper div surrounding the entire app, maintaining the exact DOM structure of 
 *     the original <Container>: the CSS-module class `Section__container___3YYTG`.
 *     Authenticator imports its theme from `Amplify-UI/Amplify-UI-Theme` (where `container = {}`), NOT 
 *     `AmplifyTheme.js` at the root package (which includes `fontFamily` and `padding: 15px`). Therefore, 
 *     the original wrapper div has no inline styles, and the app's font is inherited from `body` 
 *     ('Zen Kaku Gothic New'). Do not add `fontFamily` or `padding` here.
 */
// The CSS-module class attached to the wrapper div by the original <Authenticator> (`AmplifyUI.container` from @aws-amplify/ui@2).
// App.css relies on this exact class to set `height: 100%` across the chain: html > body > #root > wrapper div > #main-layout.
// Missing this class breaks the height chain, causing the layout not to take full screen height after login.
const AMPLIFY_UI_CONTAINER_CLASS = 'Section__container___3YYTG';

// The sole rule of the original class in @aws-amplify/ui/dist/style.css (this CSS file is no longer imported).
const CONTAINER_STYLE = {
    fontWeight: '400'
};

const AuthStateContext = createContext({ authState: 'loading', authData: null, onStateChange: () => {} });

export const useAuthState = () => useContext(AuthStateContext);

const readCachedAuthState = () => {
    try {
        return localStorage.getItem(AUTHENTICATOR_AUTHSTATE);
    } catch (e) {
        return null;
    }
};

const writeCachedAuthState = state => {
    try {
        localStorage.setItem(AUTHENTICATOR_AUTHSTATE, state);
    } catch (e) {
        /* private mode / storage bị chặn - Authenticator gốc cũng chỉ nuốt lỗi */
    }
};

const AuthStateProvider = ({ children }) => {
    const [authState, setAuthState] = useState('loading');
    const [authData, setAuthData] = useState(null);
    const isMounted = useRef(false);
    const authStateRef = useRef('loading');

    const handleStateChange = useCallback((nextState, data) => {
        const state = nextState === 'signedOut' ? 'signIn' : nextState;
        if (state === authStateRef.current) {
            return;
        }
        authStateRef.current = state;
        writeCachedAuthState(state);
        if (isMounted.current) {
            setAuthState(state);
            setAuthData(data ?? null);
        }
    }, []);

    useEffect(() => {
        isMounted.current = true;

        const removeHubListener = Hub.listen('auth', ({ payload }) => {
            // Amplify v6 renamed events: v2's 'signIn'/'signOut' became 'signedIn'/'signedOut',
            // and 'cognitoHostedUI' became 'signInWithRedirect'.
            // DO NOT handle 'signedIn': v6 fires this event directly inside Auth.signIn(), before the login flow
            // writes to localStorage / checks MFA / loads data => LoginLayout redirects to '/' prematurely (causing URL jumping).
            // v2's <Authenticator> didn't listen to Hub's 'signIn' either. Login components invoke
            // onStateChange(SIGNED_IN) themselves upon completion.
            switch (payload?.event) {
                case 'signInWithRedirect':
                    handleStateChange('signedIn', payload.data);
                    break;
                case 'signedOut':
                case 'signInWithRedirect_failure':
                    handleStateChange('signIn', null);
                    break;
                default:
                    break;
            }
        });

        Auth.currentAuthenticatedUser()
            .then(user => {
                if (isMounted.current) {
                    handleStateChange('signedIn', user);
                }
            })
            .catch(() => {
                if (!isMounted.current) {
                    return;
                }
                const cached = readCachedAuthState();
                const promise = cached === 'signedIn' ? Auth.signOut() : Promise.resolve();
                promise.catch(() => {}).then(() => handleStateChange('signIn'));
            });

        return () => {
            isMounted.current = false;
            if (typeof removeHubListener === 'function') {
                removeHubListener();
            }
        };
    }, [handleStateChange]);

    return (
        <AuthStateContext.Provider value={{ authState, authData, onStateChange: handleStateChange }}>
            <div className={`amplify-container ${AMPLIFY_UI_CONTAINER_CLASS}`} style={CONTAINER_STYLE}>
                {children}
            </div>
        </AuthStateContext.Provider>
    );
};

export const withAuthState = Component => props => {
    const { authState, authData, onStateChange } = useAuthState();
    return <Component {...props} authState={authState} authData={authData} onStateChange={onStateChange} />;
};

export default AuthStateProvider;
